import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getPlayerId } from "../utils";
import { useDailyMissions } from "./useDailyMissions";
import { apiFetch } from "../apiBase";
import { allTanks } from "../data/tanks";
import { computeTankMastery, type TankMastery } from "../data/tankMastery";
import type { TankConfig } from "../types/tanks";
export type { TankConfig } from "../types/tanks";

const syncToServer = async (state: {
  chips: number;
  crystals: number;
  totalBattles: number;
  totalWins: number;
  totalKills: number;
  tanks: TankConfig[];
  stages: StageConfig[];
  skins: SkinConfig[];
  effects: EffectConfig[];
  selectedTankId: string;
  selectedStageId: string;
  selectedSkinId: string | null;
  selectedEffectId: string | null;
}) => {
  try {
    const playerId = getPlayerId();
    const unlockedTanks = state.tanks.filter(t => t.unlocked).map(t => t.id);
    const unlockedStages = state.stages.filter(s => s.unlocked).map(s => s.id);
    const unlockedSkins = state.skins.filter(s => s.unlocked).map(s => s.id);
    const unlockedEffects = state.effects.filter(e => e.unlocked).map(e => e.id);
    
    await apiFetch("/api/progress", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        odysId: playerId,
        chips: state.chips,
        crystals: state.crystals,
        totalBattles: state.totalBattles,
        totalWins: state.totalWins,
        totalKills: state.totalKills,
        unlockedTanks,
        unlockedStages,
        unlockedSkins,
        unlockedEffects,
        selectedTankId: state.selectedTankId,
        selectedStageId: state.selectedStageId,
        selectedSkinId: state.selectedSkinId,
        selectedEffectId: state.selectedEffectId,
      }),
    });
  } catch (error) {
    console.error("Failed to sync progress to server:", error);
  }
};

export interface StageConfig {
  id: string;
  name: string;
  description: string;
  size: number;
  terrain: string;
  price: number;
  currency: "chips" | "crystals";
  unlocked: boolean;
}

export type SkinCategory = "paises" | "animais" | "militar" | "lendarias" | "neon" | "listrados" | "especiais";

export interface SkinConfig {
  id: string;
  name: string;
  description: string;
  category: SkinCategory;
  tankId: string | null; // null = universal
  preview: string; // cor ou URL da preview (CSS gradient para bandeiras)
  bodyColor?: string; // cor do corpo do tanque (para skins bicolores)
  turretColor?: string; // cor da torre do tanque (para skins bicolores)
  baseColor?: string; // cor da base do tanque (para skins tricolores ou multicolor)
  price: number;
  currency: "chips" | "crystals";
  rarity: "common" | "rare" | "epic" | "legendary";
  unlocked: boolean;
}

export interface EffectConfig {
  id: string;
  name: string;
  description: string;
  type: "trail" | "explosion" | "shot" | "aura" | "movement" | "special";
  preview: string;
  price: number;
  currency: "chips" | "crystals";
  rarity: "common" | "rare" | "epic" | "legendary";
  unlocked: boolean;
}

interface ProgressState {
  // Identificador do jogador
  odysId: string;
  
  // Moedas
  chips: number;      // Moeda de gameplay (antigo coins)
  crystals: number;   // Moeda premium
  
  // Estatísticas
  totalBattles: number;
  totalWins: number;
  totalKills: number;
  lastBattleScore: number;
  
  // Inventário
  tanks: TankConfig[];
  stages: StageConfig[];
  skins: SkinConfig[];
  effects: EffectConfig[];
  
  // Seleções
  selectedTankId: string;
  selectedStageId: string;
  selectedSkinId: string | null;
  selectedEffectId: string | null;

  // Maestria: abates acumulados por tanque (prestígio visual, nunca stats)
  tankKills: Record<string, number>;

  // Modo História - Habilidades permanentes
  astraPermanentlyUnlocked: boolean; // true após desbloquear no Cap 2
  
  isLoaded: boolean;

  // Itens de recompensa do BR aguardando uso (equipamento inicial)
  pendingBRItems: string[];
  addPendingBRItems: (ids: string[]) => void;
  consumePendingBRItem: () => string | null;

  // Ações de moedas
  addChips: (amount: number) => void;
  spendChips: (amount: number) => boolean;
  addCrystals: (amount: number) => void;
  spendCrystals: (amount: number) => boolean;
  
  // Ações de desbloqueio
  unlockTank: (tankId: string) => boolean;
  grantTank: (tankId: string) => void;
  unlockStage: (stageId: string) => boolean;
  unlockSkin: (skinId: string) => boolean;
  unlockEffect: (effectId: string) => boolean;
  
  // Ações de seleção
  selectTank: (tankId: string) => void;
  selectStage: (stageId: string) => void;
  selectSkin: (skinId: string | null) => void;
  selectEffect: (effectId: string | null) => void;
  
  // Gameplay
  recordBattle: (won: boolean, kills: number, score?: number) => void;
  
  // Modo História - Habilidades
  unlockAstraPermanently: () => void;
  isAstraPermanentlyUnlocked: () => boolean;
  
  // Getters
  getSelectedTank: () => TankConfig;
  getSelectedStage: () => StageConfig;
  getSelectedSkin: () => SkinConfig | null;
  getSelectedEffect: () => EffectConfig | null;
  getTankMastery: (tankId: string) => TankMastery;
  
  // Server sync
  loadFromServer: () => Promise<void>;
}

const defaultTanks: TankConfig[] = allTanks;

const defaultStages: StageConfig[] = [
  {
    id: "roguelike",
    name: "Roguelike",
    description: "Terreno aleatório a cada partida",
    size: 40,
    terrain: "random",
    price: 0,
    currency: "chips",
    unlocked: true,
  },
  {
    id: "industrial",
    name: "Industrial",
    description: "Fábrica metálica cyberpunk",
    size: 40,
    terrain: "industrial",
    price: 0,
    currency: "chips",
    unlocked: true,
  },
  {
    id: "desert",
    name: "Deserto",
    description: "Arena com dunas quentes",
    size: 45,
    terrain: "desert",
    price: 200,
    currency: "chips",
    unlocked: false,
  },
  {
    id: "arctic",
    name: "Ártico",
    description: "Batalha no gelo",
    size: 45,
    terrain: "arctic",
    price: 400,
    currency: "chips",
    unlocked: false,
  },
  {
    id: "forest",
    name: "Floresta",
    description: "Selva densa e perigosa",
    size: 50,
    terrain: "forest",
    price: 600,
    currency: "chips",
    unlocked: false,
  },
  {
    id: "volcanic",
    name: "Vulcânico",
    description: "Lava e destruição",
    size: 45,
    terrain: "volcanic",
    price: 800,
    currency: "chips",
    unlocked: false,
  },
  {
    id: "cyber",
    name: "Cyber",
    description: "Neon e tecnologia futurista",
    size: 50,
    terrain: "cyber",
    price: 1000,
    currency: "chips",
    unlocked: false,
  },
];

// Skins cosméticas (não afetam gameplay)
// Funcionam em Solo e Multiplayer - No modo história, o tanque segue a narrativa
// Categorias: paises, animais, militar, lendarias, neon, listrados, especiais
const defaultSkins: SkinConfig[] = [
  // ══════════════════════════════════════════════
  // === MILITAR (Military) - Camuflagens ===
  // ══════════════════════════════════════════════
  {
    id: "skin-camo-desert",
    name: "Camuflagem Deserto",
    description: "Padrão de camuflagem para terrenos áridos",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #C2B280, #8B7355, #C2B280)",
    bodyColor: "#C2B280",
    turretColor: "#8B7355",
    price: 50,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-camo-forest",
    name: "Camuflagem Floresta",
    description: "Misture-se na vegetação densa",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #228B22, #355E3B, #228B22)",
    bodyColor: "#228B22",
    turretColor: "#355E3B",
    price: 50,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-rusty",
    name: "Enferrujado",
    description: "Visual de tanque veterano de guerra",
    category: "militar",
    tankId: null,
    preview: "#8B4513",
    price: 75,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-military-camo",
    name: "Camuflagem Tática",
    description: "Padrão militar verde e marrom clássico",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #4B5320, #556B2F, #3B3C36)",
    bodyColor: "#4B5320",
    turretColor: "#556B2F",
    price: 150,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-camo-snow",
    name: "Camuflagem Neve",
    description: "Padrão ártico branco e cinza para terrenos gelados",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #E8E8E8, #B0B0B0, #E8E8E8)",
    bodyColor: "#E8E8E8",
    turretColor: "#B0B0B0",
    price: 150,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-camo-urban",
    name: "Camuflagem Urbana",
    description: "Padrão cinza digital para combate em cidades",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #708090, #4A4A4A, #696969)",
    bodyColor: "#708090",
    turretColor: "#4A4A4A",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-camo-naval",
    name: "Camuflagem Naval",
    description: "Padrão azul-marinho para operações costeiras",
    category: "militar",
    tankId: null,
    preview: "linear-gradient(135deg, #1B3A4B, #2C5F7C, #1B3A4B)",
    bodyColor: "#1B3A4B",
    turretColor: "#2C5F7C",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-stealth",
    name: "Furtivo",
    description: "Pintura anti-radar preta fosca de operações secretas",
    category: "militar",
    tankId: null,
    preview: "#1a1a2e",
    bodyColor: "#1a1a2e",
    turretColor: "#0f0f1a",
    price: 350,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === PAÍSES (Flags) - Bandeiras nacionais ===
  // ══════════════════════════════════════════════
  {
    id: "skin-flag-brazil",
    name: "Brasil",
    description: "Verde e amarelo da bandeira brasileira",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #009739, #FFDF00)",
    bodyColor: "#009739",
    turretColor: "#FFDF00",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-usa",
    name: "Estados Unidos",
    description: "Vermelho, branco e azul patriótico",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #3C3B6E, #FFFFFF, #B22234)",
    bodyColor: "#3C3B6E",
    turretColor: "#B22234",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-japan",
    name: "Japão",
    description: "Sol nascente vermelho sobre branco",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #FFFFFF, #BC002D)",
    bodyColor: "#FFFFFF",
    turretColor: "#BC002D",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-germany",
    name: "Alemanha",
    description: "Preto, vermelho e dourado",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(180deg, #000000, #DD0000, #FFCC00)",
    bodyColor: "#000000",
    turretColor: "#FFCC00",
    baseColor: "#DD0000",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-uk",
    name: "Reino Unido",
    description: "Union Jack clássica azul e vermelho",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #012169, #C8102E)",
    bodyColor: "#012169",
    turretColor: "#C8102E",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-france",
    name: "França",
    description: "Tricolor azul, branco e vermelho",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(90deg, #0055A4, #FFFFFF, #EF4135)",
    bodyColor: "#0055A4",
    turretColor: "#EF4135",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-italy",
    name: "Itália",
    description: "Verde, branco e vermelho italiano",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(90deg, #008C45, #FFFFFF, #CD212A)",
    bodyColor: "#008C45",
    turretColor: "#CD212A",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-argentina",
    name: "Argentina",
    description: "Celeste e branco com sol dourado",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(180deg, #74ACDF, #FFFFFF, #74ACDF)",
    bodyColor: "#74ACDF",
    turretColor: "#FFFFFF",
    baseColor: "#F6B40E",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-australia",
    name: "Austrália",
    description: "Azul escuro e estrelas da Commonwealth",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #012169, #0A2E6E, #FFFFFF)",
    bodyColor: "#012169",
    turretColor: "#0A2E6E",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-canada",
    name: "Canadá",
    description: "Vermelho e branco com a folha de bordo",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(90deg, #D80621, #FFFFFF, #D80621)",
    bodyColor: "#D80621",
    turretColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-china",
    name: "China",
    description: "Vermelho imperial com estrela dourada",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #DE2910, #A30000, #FFDE00)",
    bodyColor: "#DE2910",
    turretColor: "#FFDE00",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-india",
    name: "Índia",
    description: "Açafrão, branco e verde da bandeira indiana",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(180deg, #FF9933, #FFFFFF, #138808)",
    bodyColor: "#FF9933",
    turretColor: "#138808",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-indonesia",
    name: "Indonésia",
    description: "Vermelho e branco da Nusantara",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(180deg, #CE1126, #FFFFFF)",
    bodyColor: "#CE1126",
    turretColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-mexico",
    name: "México",
    description: "Verde, branco e vermelho tricolor mexicano",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(90deg, #006847, #FFFFFF, #CE1126)",
    bodyColor: "#006847",
    turretColor: "#CE1126",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-russia",
    name: "Rússia",
    description: "Faixas branca, azul e vermelha",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(180deg, #FFFFFF, #0039A6, #D52B1E)",
    bodyColor: "#0039A6",
    turretColor: "#D52B1E",
    baseColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-saudi-arabia",
    name: "Arábia Saudita",
    description: "Verde profundo com caligrafia branca",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #006C35, #0A8F49, #FFFFFF)",
    bodyColor: "#006C35",
    turretColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-south-africa",
    name: "África do Sul",
    description: "Mosaico vibrante verde, azul, vermelho e dourado",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #007A4D, #000000, #FFB81C, #DE3831, #002395)",
    bodyColor: "#007A4D",
    turretColor: "#FFB81C",
    baseColor: "#DE3831",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-south-korea",
    name: "Coreia do Sul",
    description: "Branco com destaque vermelho e azul",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #FFFFFF, #CD2E3A, #0047A0)",
    bodyColor: "#FFFFFF",
    turretColor: "#CD2E3A",
    baseColor: "#0047A0",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-flag-turkey",
    name: "Turquia",
    description: "Vermelho intenso com símbolo branco",
    category: "paises",
    tankId: null,
    preview: "linear-gradient(135deg, #E30A17, #B30000, #FFFFFF)",
    bodyColor: "#E30A17",
    turretColor: "#FFFFFF",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === ANIMAIS (Animals) - Texturas animais ===
  // ══════════════════════════════════════════════
  {
    id: "skin-stripes-tiger",
    name: "Tigre",
    description: "Listras laranja e pretas como um tigre feroz",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #FF8C00, #1a1a00, #FF8C00, #1a1a00)",
    bodyColor: "#FF8C00",
    turretColor: "#1a1a00",
    price: 120,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-stripes-zebra",
    name: "Zebra",
    description: "Padrão clássico preto e branco das savanas",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #FFFFFF, #1a1a1a, #FFFFFF, #1a1a1a)",
    bodyColor: "#FFFFFF",
    turretColor: "#1a1a1a",
    price: 100,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-snake",
    name: "Serpente",
    description: "Escamas de cobra com padrão verde e negro",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #2E8B57, #0a3d0a, #2E8B57)",
    bodyColor: "#2E8B57",
    turretColor: "#0a3d0a",
    price: 150,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-shark",
    name: "Tubarão",
    description: "Cinza oceânico com ventre branco de predador marinho",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(180deg, #5F7A8A, #C8D6D6)",
    bodyColor: "#5F7A8A",
    turretColor: "#C8D6D6",
    price: 150,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-wolf",
    name: "Lobo",
    description: "Pelagem cinza e branca do lobo das montanhas",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #6B6B6B, #D3D3D3)",
    bodyColor: "#6B6B6B",
    turretColor: "#D3D3D3",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-eagle",
    name: "Águia",
    description: "Marrom e dourado majestoso da águia real",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #5C3317, #DAA520)",
    bodyColor: "#5C3317",
    turretColor: "#DAA520",
    price: 200,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-panther",
    name: "Pantera Negra",
    description: "Negro profundo com brilho felino misterioso",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #0a0a0a, #2a0a2a, #0a0a0a)",
    bodyColor: "#0a0a0a",
    turretColor: "#2a0a2a",
    price: 350,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-phoenix",
    name: "Fênix",
    description: "Vermelho e dourado flamejante da ave lendária",
    category: "animais",
    tankId: null,
    preview: "linear-gradient(135deg, #FF4500, #FFD700, #FF4500)",
    bodyColor: "#FF4500",
    turretColor: "#FFD700",
    price: 400,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === LISTRADOS (Stripes) ===
  // ══════════════════════════════════════════════
  {
    id: "skin-stripes-racing",
    name: "Listras de Corrida",
    description: "Listras diagonais com padrão geométrico de corrida",
    category: "listrados",
    tankId: null,
    preview: "repeating-linear-gradient(-35deg, #b30000 0 18px, #ffffff 18px 30px)",
    bodyColor: "#b30000",
    turretColor: "#7a0000",
    price: 80,
    currency: "chips",
    rarity: "common",
    unlocked: false,
  },
  {
    id: "skin-stripes-candy",
    name: "Doce Listrado",
    description: "Listras rosa e brancas com detalhes geométricos doces",
    category: "listrados",
    tankId: null,
    preview: "repeating-linear-gradient(-30deg, #ff69b4 0 14px, #ffffff 14px 26px)",
    bodyColor: "#ff69b4",
    turretColor: "#ff4fa7",
    price: 150,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === NEON ===
  // ══════════════════════════════════════════════
  {
    id: "skin-neon-blue",
    name: "Neon Cibernético",
    description: "Brilho azul futurista com linhas de circuito",
    category: "neon",
    tankId: null,
    preview: "#00D4FF",
    price: 150,
    currency: "crystals",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-neon-pink",
    name: "Neon Synthwave",
    description: "Rosa vibrante estilo anos 80",
    category: "neon",
    tankId: null,
    preview: "#FF1493",
    price: 150,
    currency: "crystals",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-chrome",
    name: "Cromado",
    description: "Acabamento espelhado reflexivo",
    category: "neon",
    tankId: null,
    preview: "#C0C0C0",
    price: 200,
    currency: "crystals",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "skin-arctic",
    name: "Tempestade Ártica",
    description: "Branco gélido com detalhes de gelo",
    category: "neon",
    tankId: null,
    preview: "#E0FFFF",
    price: 200,
    currency: "crystals",
    rarity: "rare",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === ESPECIAIS (Special Patterns) ===
  // ══════════════════════════════════════════════
  {
    id: "skin-carbon-fiber",
    name: "Fibra de Carbono",
    description: "Textura premium de fibra de carbono",
    category: "especiais",
    tankId: null,
    preview: "#1a1a1a",
    price: 300,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-pixel-art",
    name: "Pixel Art",
    description: "Visual retrô 8-bits pixelizado",
    category: "especiais",
    tankId: null,
    preview: "#00FF00",
    price: 400,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-gradient-sunset",
    name: "Pôr do Sol",
    description: "Gradiente laranja e roxo do entardecer",
    category: "especiais",
    tankId: null,
    preview: "linear-gradient(135deg, #FF6B35, #9B59B6)",
    bodyColor: "#FF6B35",
    turretColor: "#9B59B6",
    price: 450,
    currency: "chips",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-inferno",
    name: "Inferno",
    description: "Chamas animadas envolvendo o tanque",
    category: "especiais",
    tankId: null,
    preview: "#FF4500",
    price: 350,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-galaxy",
    name: "Galáxia",
    description: "Padrão de nebulosa com estrelas brilhantes",
    category: "especiais",
    tankId: null,
    preview: "#1a0033",
    price: 400,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-diamond",
    name: "Diamante",
    description: "Cristais refratando luz em todas as direções",
    category: "especiais",
    tankId: null,
    preview: "#B9F2FF",
    price: 450,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "skin-toxic",
    name: "Tóxico",
    description: "Verde radioativo com efeito de brilho",
    category: "especiais",
    tankId: null,
    preview: "#39FF14",
    price: 350,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },

  // ══════════════════════════════════════════════
  // === LENDÁRIAS (Legendary) - Compradas com cristais ===
  // ══════════════════════════════════════════════
  {
    id: "skin-dragon",
    name: "Escamas de Dragão",
    description: "Textura de escamas com brilho metálico dourado",
    category: "lendarias",
    tankId: null,
    preview: "#FFD700",
    price: 750,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "skin-holographic",
    name: "Holográfico",
    description: "Efeito arco-íris que muda conforme o ângulo",
    category: "lendarias",
    tankId: null,
    preview: "linear-gradient(45deg, #ff00ff, #00ffff, #ffff00)",
    price: 850,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "skin-void",
    name: "Vazio Cósmico",
    description: "Negro absoluto com partículas de estrelas",
    category: "lendarias",
    tankId: null,
    preview: "#0a0a0a",
    price: 1000,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "skin-golden-emperor",
    name: "Imperador Dourado",
    description: "Ouro polido com detalhes em rubi",
    category: "lendarias",
    tankId: null,
    preview: "#FFD700",
    price: 1200,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
];

// Efeitos visuais (não afetam gameplay)
// Funcionam em Solo e Multiplayer - No modo história, efeitos seguem a narrativa
const defaultEffects: EffectConfig[] = [
  // ══════════════════════════════════════════════
  // === RASTROS DE TIRO — Raros (Chips) ===
  // ══════════════════════════════════════════════
  {
    id: "effect-trail-fire",
    name: "Rastro de Fogo",
    description: "Projéteis deixam rastro de chamas",
    type: "trail",
    preview: "#FF4500",
    price: 500,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "effect-trail-ice",
    name: "Rastro de Gelo",
    description: "Projéteis deixam cristais de gelo",
    type: "trail",
    preview: "#87CEEB",
    price: 500,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "effect-trail-electric",
    name: "Rastro Elétrico",
    description: "Arcos de eletricidade seguem seus tiros",
    type: "trail",
    preview: "#00BFFF",
    price: 750,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  // ══════════════════════════════════════════════
  // === MOVIMENTO — Raros (Chips) ===
  // ══════════════════════════════════════════════
  {
    id: "effect-move-smoke",
    name: "Rastro de Fumaça",
    description: "Deixe uma trilha de fumaça ao se mover",
    type: "movement",
    preview: "#888888",
    price: 600,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  {
    id: "effect-move-sparkle",
    name: "Rastro Brilhante",
    description: "Partículas cintilantes seguem seu tanque",
    type: "movement",
    preview: "#FFD700",
    price: 800,
    currency: "chips",
    rarity: "rare",
    unlocked: false,
  },
  // ══════════════════════════════════════════════
  // === ÉPICOS (Cristais) ===
  // ══════════════════════════════════════════════
  {
    id: "effect-trail-rainbow",
    name: "Rastro Arco-Íris",
    description: "Cores vibrantes seguem cada projétil",
    type: "trail",
    preview: "linear-gradient(90deg, red, orange, yellow, green, blue, purple)",
    price: 200,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-explosion-confetti",
    name: "Explosão Festiva",
    description: "Inimigos explodem em confete colorido",
    type: "explosion",
    preview: "#FF69B4",
    price: 250,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-explosion-hearts",
    name: "Explosão de Amor",
    description: "Corações voam quando você elimina inimigos",
    type: "explosion",
    preview: "#FF1493",
    price: 250,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-explosion-skull",
    name: "Marca da Caveira",
    description: "Uma caveira aparece onde inimigos são destruídos",
    type: "explosion",
    preview: "#1a1a1a",
    price: 300,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-explosion-star",
    name: "Supernova",
    description: "Explosões brilhantes como estrelas",
    type: "explosion",
    preview: "#FFD700",
    price: 275,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-move-flames",
    name: "Rastro Flamejante",
    description: "Chamas aparecem por onde você passa",
    type: "movement",
    preview: "#FF4500",
    price: 200,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  {
    id: "effect-kill-announcer",
    name: "Anunciador de Kills",
    description: "Texto animado 'ELIMINADO!' aparece em cada kill",
    type: "special",
    preview: "#FF0000",
    price: 400,
    currency: "crystals",
    rarity: "epic",
    unlocked: false,
  },
  // ══════════════════════════════════════════════
  // === LENDÁRIOS (Cristais) ===
  // ══════════════════════════════════════════════
  {
    id: "effect-aura-fire",
    name: "Aura de Fogo",
    description: "Chamas dançam ao redor do seu tanque",
    type: "aura",
    preview: "#FF4500",
    price: 500,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-aura-electric",
    name: "Aura Elétrica",
    description: "Relâmpagos circulam seu tanque constantemente",
    type: "aura",
    preview: "#00BFFF",
    price: 500,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-aura-shadow",
    name: "Aura Sombria",
    description: "Névoa negra misteriosa envolve seu tanque",
    type: "aura",
    preview: "#1a0033",
    price: 600,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  // ══════════════════════════════════════════════
  // === LENDÁRIOS PREMIUM (Cristais — Novos!) ===
  // ══════════════════════════════════════════════
  {
    id: "effect-aura-singularity",
    name: "Singularidade",
    description: "Um buraco negro em miniatura orbita seu tanque, distorcendo a luz ao redor",
    type: "aura",
    preview: "linear-gradient(135deg, #0a0014, #2d004d, #0a0014)",
    price: 1200,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-trail-dimensional",
    name: "Fissura Dimensional",
    description: "Projéteis rasgam o tecido do espaço-tempo, deixando fendas brilhantes",
    type: "trail",
    preview: "linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)",
    price: 1000,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-aura-plasma",
    name: "Plasma Vivo",
    description: "Energia de plasma pulsante envolve o tanque como uma segunda pele magnética",
    type: "aura",
    preview: "linear-gradient(135deg, #00ff88, #0088ff, #00ff88)",
    price: 1500,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-explosion-antimatter",
    name: "Detonação Antimatéria",
    description: "Explosões invertem cores do cenário por 1s — impacto visual devastador",
    type: "explosion",
    preview: "linear-gradient(135deg, #000000, #ffffff, #000000)",
    price: 1200,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-move-warp",
    name: "Distorção Warp",
    description: "O espaço se dobra atrás do tanque como viagem FTL — efeito de velocidade extrema",
    type: "movement",
    preview: "linear-gradient(90deg, #1a0033, #4400aa, #8800ff)",
    price: 1000,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-trail-phoenix",
    name: "Trilha da Fênix",
    description: "Projéteis deixam asas de fogo dourado que se dissipam como cinzas",
    type: "trail",
    preview: "linear-gradient(135deg, #ff4500, #ffd700, #ff4500)",
    price: 800,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-aura-aurora",
    name: "Aurora Boreal",
    description: "Faixas de luz verde e roxa dançam sobre seu tanque como a aurora polar",
    type: "aura",
    preview: "linear-gradient(135deg, #00ff88, #8800ff, #00ffcc)",
    price: 900,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-explosion-shockwave",
    name: "Onda de Choque",
    description: "Anéis de energia expandem do ponto de impacto como pulso gravitacional",
    type: "explosion",
    preview: "linear-gradient(135deg, #00bfff, #ffffff, #00bfff)",
    price: 700,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-special-killstreak",
    name: "Série de Eliminações",
    description: "Contador holográfico de kills em sequência com efeitos crescentes a cada 3",
    type: "special",
    preview: "linear-gradient(135deg, #ff0000, #ff4400, #ffaa00)",
    price: 1500,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-move-glitch",
    name: "Glitch Digital",
    description: "Tanque deixa cópias holográficas fragmentadas que piscam e desaparecem",
    type: "movement",
    preview: "linear-gradient(135deg, #00ff00, #ff0066, #0066ff)",
    price: 1100,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
  {
    id: "effect-aura-void",
    name: "Manto do Vazio",
    description: "O tanque parece absorver a luz — campo de escuridão com partículas de estrelas",
    type: "aura",
    preview: "linear-gradient(135deg, #000011, #110033, #000011)",
    price: 2000,
    currency: "crystals",
    rarity: "legendary",
    unlocked: false,
  },
];

export const useProgress = create<ProgressState>()(
  persist(
    (set, get) => ({
      // Identificador do jogador
      odysId: getPlayerId(),
      
      // Moedas
      chips: 100,
      crystals: 0,
      
      // Estatísticas
      totalBattles: 0,
      totalWins: 0,
      totalKills: 0,
      lastBattleScore: 0,
      
      // Inventário
      tanks: defaultTanks,
      stages: defaultStages,
      skins: defaultSkins,
      effects: defaultEffects,
      
      // Seleções
      selectedTankId: "standard",
      selectedStageId: "roguelike",
      selectedSkinId: null,
      selectedEffectId: null,

      // Maestria
      tankKills: {},

      // Modo História - Habilidades
      astraPermanentlyUnlocked: false,
      
      isLoaded: false,

      pendingBRItems: [],

      loadFromServer: async () => {
        try {
          const playerId = getPlayerId();
          const response = await apiFetch(`/api/progress/${playerId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            const { stages, skins, effects } = get();
            
            // Migrar coins antigo para chips se existir
            const chipsValue = data.chips ?? data.coins ?? 100;
            
            // Sempre usar defaultTanks para garantir todos os 105 tanques
            const updatedTanks = defaultTanks.map(t => ({
              ...t,
              unlocked: data.unlockedTanks?.includes(t.id) || t.id === "standard",
            }));
            
            console.log(`[Progress] Loaded ${updatedTanks.length} tanks from server sync`);
            
            const updatedStages = stages.map(s => ({
              ...s,
              unlocked: data.unlockedStages?.includes(s.id) || s.id === "roguelike" || s.id === "industrial",
            }));
            
            // Sempre reconstruir skins/effects a partir dos defaults para corrigir categorias
            const updatedSkins = defaultSkins.map(s => ({
              ...s,
              unlocked: data.unlockedSkins?.includes(s.id) || false,
            }));

            const updatedEffects = defaultEffects.map(e => ({
              ...e,
              unlocked: data.unlockedEffects?.includes(e.id) || false,
            }));
            
            set({
              chips: chipsValue,
              crystals: data.crystals ?? 0,
              totalBattles: data.totalBattles ?? 0,
              totalWins: data.totalWins ?? 0,
              totalKills: data.totalKills ?? 0,
              tanks: updatedTanks,
              stages: updatedStages,
              skins: updatedSkins,
              effects: updatedEffects,
              selectedTankId: data.selectedTankId ?? "standard",
              selectedStageId: data.selectedStageId ?? "roguelike",
              selectedSkinId: data.selectedSkinId ?? null,
              selectedEffectId: data.selectedEffectId ?? null,
              isLoaded: true,
            });
          } else {
            console.warn(`Failed to load progress: ${response.status} ${response.statusText}`);
            set({ isLoaded: true });
          }
        } catch (error) {
          console.error("Failed to load progress from server:", error);
          set({ isLoaded: true });
        }
      },
      
      // === Itens de recompensa do Battle Royale ===
      // Itens ganhos na tela de vitória viram equipamento inicial das
      // próximas partidas BR (fila persistida, consumida em startBattleRoyale).
      addPendingBRItems: (ids) => {
        if (!ids.length) return;
        set((state) => ({ pendingBRItems: [...state.pendingBRItems, ...ids].slice(0, 20) }));
      },

      consumePendingBRItem: () => {
        const { pendingBRItems } = get();
        if (pendingBRItems.length === 0) return null;
        const [next, ...rest] = pendingBRItems;
        set({ pendingBRItems: rest });
        return next;
      },

      // === Ações de Chips ===
      addChips: (amount) => {
        if (amount > 0) {
          useDailyMissions.getState().updateProgress("earn_chips", amount);
        }
        set((state) => ({ chips: state.chips + amount }));
        setTimeout(() => syncToServer(get()), 100);
      },
      
      spendChips: (amount) => {
        const { chips } = get();
        if (chips >= amount) {
          set({ chips: chips - amount });
          syncToServer(get());
          return true;
        }
        return false;
      },
      
      // === Ações de Crystals ===
      addCrystals: (amount) => {
        set((state) => ({ crystals: state.crystals + amount }));
        setTimeout(() => syncToServer(get()), 100);
      },
      
      spendCrystals: (amount) => {
        const { crystals } = get();
        if (crystals >= amount) {
          set({ crystals: crystals - amount });
          syncToServer(get());
          return true;
        }
        return false;
      },
      
      // === Desbloqueios ===
      unlockTank: (tankId) => {
        const { tanks, spendChips, spendCrystals } = get();
        const tank = tanks.find((t) => t.id === tankId);
        if (!tank || tank.unlocked) return false;
        
        const spend = tank.currency === "crystals" ? spendCrystals : spendChips;
        if (spend(tank.price)) {
          set({
            tanks: tanks.map((t) =>
              t.id === tankId ? { ...t, unlocked: true } : t
            ),
          });
          syncToServer(get());
          return true;
        }
        return false;
      },

      grantTank: (tankId) => {
        const { tanks } = get();
        const tank = tanks.find((t) => t.id === tankId);
        if (!tank || tank.unlocked) return;
        set({
          tanks: tanks.map((t) =>
            t.id === tankId ? { ...t, unlocked: true } : t
          ),
        });
        syncToServer(get());
      },

      unlockStage: (stageId) => {
        const { stages, spendChips, spendCrystals } = get();
        const stage = stages.find((s) => s.id === stageId);
        if (!stage || stage.unlocked) return false;
        
        const spend = stage.currency === "crystals" ? spendCrystals : spendChips;
        if (spend(stage.price)) {
          set({
            stages: stages.map((s) =>
              s.id === stageId ? { ...s, unlocked: true } : s
            ),
          });
          syncToServer(get());
          return true;
        }
        return false;
      },
      
      unlockSkin: (skinId) => {
        const { skins, spendChips, spendCrystals } = get();
        const skin = skins.find((s) => s.id === skinId);
        if (!skin || skin.unlocked) return false;
        
        const spend = skin.currency === "crystals" ? spendCrystals : spendChips;
        if (spend(skin.price)) {
          set({
            skins: skins.map((s) =>
              s.id === skinId ? { ...s, unlocked: true } : s
            ),
          });
          syncToServer(get());
          return true;
        }
        return false;
      },
      
      unlockEffect: (effectId) => {
        const { effects, spendChips, spendCrystals } = get();
        const effect = effects.find((e) => e.id === effectId);
        if (!effect || effect.unlocked) return false;
        
        const spend = effect.currency === "crystals" ? spendCrystals : spendChips;
        if (spend(effect.price)) {
          set({
            effects: effects.map((e) =>
              e.id === effectId ? { ...e, unlocked: true } : e
            ),
          });
          syncToServer(get());
          return true;
        }
        return false;
      },
      
      // === Seleções ===
      selectTank: (tankId) => {
        const { tanks } = get();
        const tank = tanks.find((t) => t.id === tankId);
        if (tank && tank.unlocked) {
          set({ selectedTankId: tankId });
          // [SPECIALS] Sala multiplayer em lobby ouve e revalida o poder
          window.dispatchEvent(new CustomEvent("tankSelected", { detail: { tankId } }));
          syncToServer(get());
        }
      },
      
      selectStage: (stageId) => {
        const { stages } = get();
        const stage = stages.find((s) => s.id === stageId);
        if (stage && stage.unlocked) {
          set({ selectedStageId: stageId });
          syncToServer(get());
        }
      },
      
      selectSkin: (skinId) => {
        if (skinId === null) {
          set({ selectedSkinId: null });
          syncToServer(get());
          window.dispatchEvent(new CustomEvent("skinSelected", { detail: { skinId: "" } }));
          return;
        }
        const { skins } = get();
        const skin = skins.find((s) => s.id === skinId);
        if (skin && skin.unlocked) {
          set({ selectedSkinId: skinId });
          syncToServer(get());
          window.dispatchEvent(new CustomEvent("skinSelected", { detail: { skinId } }));
        }
      },
      
      selectEffect: (effectId) => {
        if (effectId === null) {
          set({ selectedEffectId: null });
          syncToServer(get());
          // Sala multiplayer ouve para sincronizar o efeito com os outros jogadores
          window.dispatchEvent(new CustomEvent("effectSelected", { detail: { effectId: "" } }));
          return;
        }
        const { effects } = get();
        const effect = effects.find((e) => e.id === effectId);
        if (effect && effect.unlocked) {
          set({ selectedEffectId: effectId });
          syncToServer(get());
          window.dispatchEvent(new CustomEvent("effectSelected", { detail: { effectId } }));
        }
      },
      
      // === Gameplay ===
      recordBattle: (won, kills, score) => {
        const chipsEarned = 10 + kills * 5 + (won ? 20 : 0);
        set((state) => ({
          totalBattles: state.totalBattles + 1,
          totalWins: state.totalWins + (won ? 1 : 0),
          totalKills: state.totalKills + kills,
          chips: state.chips + chipsEarned,
          lastBattleScore: score ?? 0,
          // Maestria: os abates da partida contam para o tanque usado
          tankKills: kills > 0
            ? {
                ...state.tankKills,
                [state.selectedTankId]: (state.tankKills[state.selectedTankId] ?? 0) + kills,
              }
            : state.tankKills,
        }));
        syncToServer(get());
      },
      
      // === Modo História - Habilidades ===
      unlockAstraPermanently: () => {
        if (!get().astraPermanentlyUnlocked) {
          set({ astraPermanentlyUnlocked: true });
          syncToServer(get());
        }
      },
      
      isAstraPermanentlyUnlocked: () => {
        return get().astraPermanentlyUnlocked;
      },
      
      // === Getters ===
      getSelectedTank: () => {
        const { tanks, selectedTankId } = get();
        return tanks.find((t) => t.id === selectedTankId) || tanks[0];
      },
      
      getSelectedStage: () => {
        const { stages, selectedStageId } = get();
        return stages.find((s) => s.id === selectedStageId) || stages[0];
      },
      
      getSelectedSkin: () => {
        const { skins, selectedSkinId } = get();
        if (!selectedSkinId) return null;
        return skins.find((s) => s.id === selectedSkinId) || null;
      },
      
      getSelectedEffect: () => {
        const { effects, selectedEffectId } = get();
        if (!selectedEffectId) return null;
        return effects.find((e) => e.id === selectedEffectId) || null;
      },

      getTankMastery: (tankId) => {
        return computeTankMastery(get().tankKills[tankId] ?? 0);
      },
    }),
    {
      name: "rocketanks-progress",
      version: 6, // v6: catálogo canônico de tanques (famílias/specials) + maestria
      partialize: (state) => ({
        chips: state.chips,
        crystals: state.crystals,
        totalBattles: state.totalBattles,
        totalWins: state.totalWins,
        totalKills: state.totalKills,
        tanks: state.tanks,
        stages: state.stages,
        skins: state.skins,
        effects: state.effects,
        selectedTankId: state.selectedTankId,
        selectedStageId: state.selectedStageId,
        selectedSkinId: state.selectedSkinId,
        selectedEffectId: state.selectedEffectId,
        tankKills: state.tankKills,
        astraPermanentlyUnlocked: state.astraPermanentlyUnlocked,
        pendingBRItems: state.pendingBRItems,
      }),
      migrate: (persistedState: any, version: number) => {
        // Sempre sincronizar tanques com defaultTanks
        if (persistedState.tanks) {
          const savedUnlockedTanks = new Set(
            persistedState.tanks.filter((t: any) => t.unlocked).map((t: any) => t.id)
          );
          persistedState.tanks = defaultTanks.map(t => ({
            ...t,
            unlocked: savedUnlockedTanks.has(t.id) || t.id === "standard",
          }));
          console.log(`[Progress] Migrated tanks: ${persistedState.tanks.length}`);
        }

        // Sempre reconstruir skins a partir dos defaults (corrige categorias e propriedades)
        if (persistedState.skins) {
          const savedUnlockedSkins = new Set(
            persistedState.skins.filter((s: any) => s.unlocked).map((s: any) => s.id)
          );
          persistedState.skins = defaultSkins.map(s => ({
            ...s,
            unlocked: savedUnlockedSkins.has(s.id),
          }));
          console.log(`[Progress] Migrated skins: ${persistedState.skins.length}`);
        }

        // Sempre reconstruir effects a partir dos defaults
        if (persistedState.effects) {
          const savedUnlockedEffects = new Set(
            persistedState.effects.filter((e: any) => e.unlocked).map((e: any) => e.id)
          );
          persistedState.effects = defaultEffects.map(e => ({
            ...e,
            unlocked: savedUnlockedEffects.has(e.id),
          }));
          console.log(`[Progress] Migrated effects: ${persistedState.effects.length}`);
        }

        // v6: maestria por tanque
        if (!persistedState.tankKills) {
          persistedState.tankKills = {};
        }

        return persistedState;
      },
    }
  )
);
